import { useTheme, type ThemeNames } from '../../../theme';
import { Icon } from '@doxyme/icons';
import { useIntl } from '@doxyme/locale';
import { useStatusStyles } from './status.styles';
import { Spinner as SpinnerComponent, type SpinnerMode } from '../spinner';

import type { JSX } from 'react';

export type NotificationLevel =
  | 'success'
  | 'warning'
  | 'info'
  | 'error'
  | 'loader'
  | 'spinner';

type CommonProps = {
  color?: string;
  className?: string;
  size?: number;
  themeName?: ThemeNames;
};

type DefaultProps = {
  statusLevel: Exclude<NotificationLevel, 'loader'>;
  value?: never;
  mode?: never;
};

type LoaderProps = {
  statusLevel: 'loader';
  value: number;
  mode?: SpinnerMode;
};

export type StatusProps = CommonProps & (DefaultProps | LoaderProps);

export const Status = ({
  statusLevel,
  color,
  className,
  themeName,
  size = 24,
  value,
  mode = 'green',
}: StatusProps): JSX.Element | null => {
  const theme = useTheme(themeName);
  const { formatMessage } = useIntl();
  const styles = useStatusStyles();

  /**
   * The filled icon variants use a single compound SVG path where the inner
   * symbol (checkmark, exclamation, etc.) is "cut out" via winding rules,
   * leaving it transparent. To give the cutout a visible fill color, we render
   * a backing SVG circle (r=9, slightly inset from the icon's r=10 circle) in
   * the same coordinate space so they rasterize identically — avoiding the
   * subpixel mismatch that CSS backgrounds (e.g. radial-gradient) can exhibit
   * at certain browser zoom levels.
   */
  const renderFilledIcon = ({
    IconComponent,
    iconColor,
    fillColor,
  }: {
    IconComponent: typeof Icon.Checkmark;
    iconColor: string;
    fillColor: string;
  }) => (
    <span
      css={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}
      className={className}
    >
      <svg
        viewBox='0 0 24 24'
        width={size}
        height={size}
        css={{ position: 'absolute' }}
        aria-hidden
      >
        <circle cx='12' cy='12' r='9' fill={fillColor} />
      </svg>
      <IconComponent
        css={{
          ...styles.icon,
          position: 'relative',
          path: { fillRule: 'evenodd' },
        }}
        style={{ color: iconColor }}
        type='filled'
        size={size}
        color={iconColor}
        aria-hidden
      />
    </span>
  );

  switch (statusLevel) {
    case 'success': {
      return renderFilledIcon({
        IconComponent: Icon.Checkmark,
        iconColor: color ?? theme.status.positive,
        fillColor: theme.status.baseDark,
      });
    }
    case 'warning': {
      return renderFilledIcon({
        IconComponent: Icon.Error,
        iconColor: color ?? theme.status.notice,
        fillColor: theme.status.baseDark,
      });
    }
    case 'info': {
      return renderFilledIcon({
        IconComponent: Icon.Info,
        iconColor: color ?? theme.status.informative,
        fillColor: theme.status.baseLight,
      });
    }
    case 'error': {
      return renderFilledIcon({
        IconComponent: Icon.Error,
        iconColor: color ?? theme.status.negative,
        fillColor: theme.status.baseLight,
      });
    }
    case 'spinner': {
      return (
        <SpinnerComponent
          disableShrink={false}
          size={size - 4}
          mode={mode}
          aria-label={formatMessage({ id: 'status.loading' })}
        />
      );
    }
    case 'loader': {
      return (
        <SpinnerComponent
          value={value}
          variant='determinate'
          disableShrink={false}
          size={size - 4}
          mode={mode}
          aria-label={formatMessage({ id: 'status.loading' })}
        />
      );
    }
    default: {
      return null;
    }
  }
};
